import './signOutPage.scss';

import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { isEmpty, toString } from 'lodash';
import { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import heroReferenceMessage from '../../assets/hero-reference-message.svg';
import logo from '../../assets/logo-default.svg';
import { PhoneNumberInput } from '../../components/PhoneNumberInput/PhoneNumberInput';
import useApiStore from '../../store/apiStore/useApiStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../store/pinStore/usePinStore';
import useSignOutStore from '../../store/signOutStore/useSignOutStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { deliverPin, deliverPinFailedMessage } = usePinStore();
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { token } = useParameterStore();
  const { signOutMessage, signOut } = useSignOutStore();

  const form = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
  });

  const {
    handleSubmit,
    formState: { errors },
  } = form;

  useEffect(() => {
    if (user || token) {
      signOut();
    }
  }, [user, token]);

  const handleRedirect = (): void => {
    usePinStore.setState({ deliverPinFailedMessage: null });
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { primaryPhoneNumberValue } = values;

    useSignOutStore.setState({ signOutMessage: null });

    await deliverPin({
      deliverPinLocation: DeliverPinLocation.SIGN_IN,
      phone: primaryPhoneNumberValue,
      purpose: AuthType.ACCOUNT_SIGN_IN_USING_PIN,
    });

    /*
      DeliverPin sets the deliverPinSuccess in state, the
      .getState() call forces a re-render to pick up the new value
    */
    if (usePinStore.getState().deliverPinSuccess) {
      navigate('/sign-in/pin-verify');
    }
  };

  return (
    <div className="sign-out-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.signOut.header.root}>
            <img alt={t('imageAlts.logo')} src={logo} />
            <Link
              data-testid={locator.pages.signOut.header.signupLink}
              to="/sign-up"
              onClick={handleRedirect}
            >
              {t('SignOutPage.signup')}
            </Link>
          </header>

          <main>
            <figure>
              <img
                alt={t('imageAlts.heroReference')}
                src={heroReferenceMessage}
              />
            </figure>
            <form
              data-testid={locator.pages.signOut.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.signOut.form.h1}>
                  {t('SignOutPage.title')}
                </h1>

                {(deliverPinFailedMessage || signOutMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={locator.pages.signOut.alert.error}
                      icon={false}
                      severity="error"
                    >
                      <Trans
                        components={{
                          signup: (
                            <Link
                              data-testid={
                                locator.pages.signOut.alert.signupLink
                              }
                              to="/sign-up"
                              onClick={handleRedirect}
                            />
                          ),
                        }}
                      >
                        {deliverPinFailedMessage || signOutMessage}
                      </Trans>
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <PhoneNumberInput
                    form={form}
                    inputLabelKey={'SignOutPage.phoneNumber'}
                    inputName="primaryPhoneNumberValue"
                    inputProps={{
                      'data-testid': locator.pages.signOut.form.phoneInput,
                    }}
                  />

                  {!isEmpty(errors.primaryPhoneNumberValue) && (
                    <span
                      data-testid={locator.pages.signOut.form.phoneInputError}
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.primaryPhoneNumberValue.message)}
                    </span>
                  )}
                </div>

                <LoadingButton
                  color="primary"
                  data-testid={locator.pages.signOut.form.submitButton}
                  loading={isLoading}
                  loadingPosition="start"
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('SignOutPage.continue')}
                </LoadingButton>

                <p className="form-sign-up">
                  {t('SignOutPage.noAccount')}&nbsp;
                  <Link
                    data-testid={locator.pages.signOut.form.signupLink}
                    to="/sign-up"
                    onClick={handleRedirect}
                  >
                    {t('SignOutPage.signup')}
                  </Link>
                </p>
              </div>
              <footer data-testid={locator.pages.signOut.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'SignOutPage';
